import apiService from '../../Services/APIService';
import { getMasterDataFromConfig } from '../../Utils/Storage';
import { toast } from 'sonner';
import { checkAPIResponse } from '../../Utils/APIResponseUtils';

const masterConfigMapping = {
  sources: { storageName: "Sources", keyForSP: "INQSRC" },
  cities: { storageName: "Cities", keyForSP: "CITY" },
  connExtraFields: { storageName: "ConnFields", keyForSP: "ADDCONNFIELDS" },
  rejectionReasons: { storageName: "RejRsns", keyForSP: "REJRSN" },
  disqlReasons: { storageName: "DisqualRsns", keyForSP: "DISQLRSN" },
  inactiveReasons: { storageName: "InactRsns", keyForSP: "INACTRSN" },
  segments: { storageName: "Segments", keyForSP: "SEG" },
  taskTypes: { storageName: "TaskTypes", keyForSP: "TSKTYP" },
  threadStages: { storageName: "ThreadStages", keyForSP: "THRDS" },
  recordTypes: { storageName: "RecTypes", keyForSP: "RECTYP" },
  regions: { storageName: "Regions", keyForSP: "RGN" },
  templates: { storageName: "WspTemplates", keyForSP:"TMP"}
}

// Helper function to get current items from localStorage
const getExistingMasterData = (configInfo) => {
  const currentData = getMasterDataFromConfig(configInfo.storageName);

  if (Array.isArray(currentData)) {
    return currentData;
  }

  if (typeof currentData === 'string') {
    if (currentData === '' || currentData === '[]') {
      return [];
    }
    const cleanData = currentData.replace(/^\[\],?/, '');
    return cleanData ? cleanData.split(',').map(item => item.trim()).filter(item => item !== '') : [];
  }

  return [];
};

// Helper function to format items for API
const formatItemsForAPI = (items) => {
  return items.length === 0 ? '' : items.join(',');
};

// Add master data item
export const saveMastersConfigAPI = async (configType, actType, configData) => {
  let masterConfigInfo = masterConfigMapping[configType];

  try {
    let isConnExtraFields = configType == "connExtraFields";
    let isThrExtraFields = configType == "thrExtraFields";
    let isTypeFields = configType == "recordTypes" || configType == "taskTypes";
    let isRejRsns = configType == "rejectionReasons";
    let isDisqualRsns = configType == "disqlReasons";
    let isRegionField = configType == "regions";
    let isSegmentField = configType == "segments";
    let isThreadStages = configType == "threadStages";
    let isSourceField = configType == "sources";
	let isTemplateField = configType == "templates";
    let currConfigVal = isConnExtraFields ? getMasterDataFromConfig("ConnFields", true) : !isThrExtraFields ? getExistingMasterData(masterConfigInfo) : [];
    const newConfigVal = typeof configData == 'string' && configData.trim();
    var payloadPrepare = {
      changeType: "MASTER",
      key: "",
      value: '',
      values: '[]'
    }
    if (!masterConfigInfo && !isThrExtraFields) {
      throw new Error("Please select a valid configuration type.");
    }

    if (isConnExtraFields) {
      if (actType == "add") {
        payloadPrepare.key = masterConfigInfo.keyForSP;
        // Handle both object (from new Dialog) and string (legacy) inputs
        const fieldName = typeof configData === 'object' ? configData.F : configData;
        payloadPrepare.values = JSON.stringify({ F: fieldName });
      }
      else if (actType == "update") {
        payloadPrepare.key = 'UPDCONNFIELDS';
        // Ensure FID is an integer and F is the name
        const updatePayload = {
          FID: parseInt(configData.FID),
          F: configData.F
        };
        payloadPrepare.values = JSON.stringify(updatePayload);
      }
      else if (actType == "delete") {
        payloadPrepare.key = 'DLTCONNFIELDS';
        // Handle object (from new Dialog) or string (legacy) inputs
        const fieldName = typeof configData === 'object' ? configData.F : configData;
        // Find existing field to get FID, or use passed FID if available
        const fieldObj = currConfigVal.find(field => (field.F || field) === fieldName);
        payloadPrepare.value = fieldObj ? fieldObj.FID : (configData.FID || 0);
      }
    }
    else if (isTypeFields) {
      if (actType == "add") {
        payloadPrepare.key = 'ADDRECTYP';
        payloadPrepare.values = JSON.stringify(
          [{
            Typ: configData.typ,
            IsInxn: configData.isInxn ? 1 : 0,
            HasGeo: configData.hasGeo ? 1 : 0
          }]
        )
      }
      else if (actType == "delete") {
        payloadPrepare.key = 'DLTRECTYP';
        payloadPrepare.value = currConfigVal.find(field => field.Typ === configData).TID;
      }
    }
    else if (isRejRsns) {
      if ('tID' in configData && 'rsns' in configData) {
        payloadPrepare.changeType = "REJRSN";
        payloadPrepare.key = configData.tID;
        payloadPrepare.values = JSON.stringify(configData.rsns);
        console.log(payloadPrepare);
      }
      else {
        throw new Error(`Error occured while ${actType} rejection reason`);
      }
    } else if (isDisqualRsns) {
      if ('tID' in configData && 'rsns' in configData) {
        if (actType == "add") {
          payloadPrepare.key = "ADDDISQL";
          payloadPrepare.values = JSON.stringify({
            TID: configData.tID,
            RID: 0,
            R: configData.rsns[configData.rsns.length - 1].R
          });
        } else if (actType == "delete") {
          payloadPrepare.key = "DLTDISQL";
          payloadPrepare.values = JSON.stringify({
            TID: configData.tID,
            RID: configData.rsns[0]?.RID || 0,
            R: ""
          });
        }
      } else {
        throw new Error(`Error occurred while ${actType} disqualification reason`);
      }
    }
    else if (isRegionField) {
      if (actType == "add") {
        payloadPrepare.changeType = "MASTER"
        payloadPrepare.key = "ADDRGN"
        payloadPrepare.value = configData
      } else if (actType == "delete") {
        payloadPrepare.changeType = "MASTER"
        payloadPrepare.key = "DLTRGN"
        payloadPrepare.value = currConfigVal.find(field => field.Rgn === configData)?.RID;
      }
    } else if (isSegmentField) {
      const ctgData = {
        CID: 0,
        CNm: configData.CNm,
        Clr: configData.Clr.replace('#', '')
      }
      if (actType == "add") {
        payloadPrepare.key = "ADDCTG";
      } else if (actType == "update") {
        payloadPrepare.key = "UPDCTG";
        ctgData.CID = configData.CID;
      } else if (actType == "delete") {
        payloadPrepare.key = "DLTCTG";
        ctgData.CID = configData.CID;
      }
      payloadPrepare.values = JSON.stringify(ctgData);
    }
    else if (isSourceField) {
      if (actType == "add") {
        payloadPrepare.changeType = "MASTER"
        payloadPrepare.key = "ADDSRC"
        payloadPrepare.value = JSON.stringify([{ TID: 1, Src: configData, SID: 0 }])
      } else if (actType == "delete") {
        const tID = 1; // since you have only one TID in your structure

        // Find the SID of the matching Src
        const targetSrc =
          currConfigVal?.find(item => item.TID === tID)
            ?.Srcs?.find(s => s.Src.toLowerCase() === configData.toLowerCase());

        if (targetSrc) {
          payloadPrepare.changeType = "MASTER";
          payloadPrepare.key = "DLTSRC";
          payloadPrepare.value = JSON.stringify([{ TID: tID, Src: '', SID: targetSrc.SID }]);
        } else {
          console.warn("Source not found in current config:", configData);
        }
      }
    }
    else if (isThrExtraFields) {
      if (actType == "add") {
        payloadPrepare.key = 'ADDTHRFIELDS';
      }
      else if (actType == "update") {
        payloadPrepare.key = 'UPDTHRFIELDS';
      }
      else if (actType == "delete") {
        payloadPrepare.key = 'DLTTHRFIELDS';
      }
      payloadPrepare.values = JSON.stringify(configData);
    }
    else if (isThreadStages) {
      if (actType == "update") {
        payloadPrepare.key = "UPDSTG";
        payloadPrepare.values = JSON.stringify({
          TID: configData?.tID,
          SID: configData?.sID,
          S: configData?.stageName,
          D: configData?.days || 0,
          C: configData?.chances || 0
        });
      } else if (actType == "bulkUpdate") {
        payloadPrepare.key = "BULKUPDSTG";
        payloadPrepare.values = JSON.stringify({
          TID: configData?.tID,
          Stages: configData?.stages
        });
      }
    }
	else if (isTemplateField) {
		if(actType == "update"){
			payloadPrepare.key = "UPDTMP";
			payloadPrepare.values = JSON.stringify({
				TMID: configData?.TMID,
				Type: configData?.Type,
				TN : configData?.TN,
				T: configData?.T
			})
		}
		else if(actType == "add"){
			payloadPrepare.key = "ADDTMP";
			payloadPrepare.values = JSON.stringify({
				Type: configData?.Type,
				TN : configData?.TN,
				T: configData?.T
			})
		}
		else if(actType == "delete"){
			payloadPrepare.key = "DLTTMP";
			payloadPrepare.values = JSON.stringify({
				TMID: configData?.TMID
			})
		}
	}
    else {
      switch (actType) {
        case "add":
          if (!currConfigVal.includes(newConfigVal)) {
            currConfigVal.push(newConfigVal);
          }
          break;

        case "delete":
          currConfigVal = isConnExtraFields ? currConfigVal : currConfigVal.filter(item => item !== newConfigVal);
          break;

        default:
          throw new Error(`Unsupported action type: ${actType}`);
      }

      payloadPrepare.key = masterConfigInfo.keyForSP;
      payloadPrepare.value = formatItemsForAPI(currConfigVal);
    }

    const result = await apiService.sendChange(3, "set", payloadPrepare);
    checkAPIResponse(result);
    return result;

  } catch (error) {
    throw error;
  }
};

export const manageProductAPI = async (actType, data) => {
  const configData = {
    changeType: 'MASTER',
    key: '',
    value: '',
    values: '[]'
  }

  // Build Acpt string based on allow options
  let acptString = '';
  if (data?.allowInquiry) acptString += 'I';
  if (data?.allowOrder) acptString += 'O';
  if (data?.allowSupport) acptString += 'S';

  if (actType == 'add') {
    configData.key = 'ADDPRD';
    configData.values = JSON.stringify([{
      PID: 0,
      PNm: data?.name,
      Img: data?.imageUrl || '',
      Unit: data?.unit || '',
      D: data?.description,
      Rate: parseFloat(data?.price) || 0,
      PTID: data?.offeringType || 1,
      Mdls: data?.models || [],
      Acpt: acptString || 'IOS'
    }])
  }
  else if (actType == 'update') {
    configData.key = 'UPDPRD';
    configData.values = JSON.stringify({
      PID: data?.pid,
      PNm: data?.name,
      Img: data?.imageUrl || '',
      Unit: data?.unit || '',
      D: data?.description,
      Rate: parseFloat(data?.price) || 0,
      PTID: data?.offeringType || 1,
      Mdls: data?.models || [],
      Acpt: acptString || 'IOS'
    })
  }
  else if (actType == 'delete') {
    configData.key = 'DLTPRD';
    configData.value = data;
  }

  try {
    const extraParams = { isWeb: true };
    const result = await apiService.sendChange(3, 'set', configData, extraParams);
    checkAPIResponse(result);
    return result;

  } catch (error) {
    toast.error(error.message || `Failed to ${actType} product`);
    throw error;
  }

}


